
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  SearchInput,
  Margin,
  Title,
  Size,
  Paragraph,
  Text,
  Flex,
  FlexItem,
  FlexAlign,
} from '@drawbotics/react-drylus';
import sv from '@drawbotics/drylus-style-vars';
import { useState, useEffect } from 'react';
import Playground, { Preview, CodeBox } from '~/components/Playground';
import { ControlledField, useDebounce } from '../utils';
export function searchCharacters(search) {
  const apiKey = '285962f014e9da78f66a9b1e41a82dcf';
  const queryString = `apikey=${apiKey}&titleStartsWith=${search}`;
  return fetch(`https://gateway.marvel.com/v1/public/comics?${queryString}&limit=10`, {
    method: 'GET'
  }).then(r => r.json()).then(r => r.data.results).catch(error => {
    console.error(error);
    return [];
  });
}
export const Example = () => {
  const [comic, setComic] = useState();
  const [keywords, setKeywords] = useState('');
  const [results, setResults] = useState();
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(keywords, 500);
  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      setIsSearching(true);
      searchCharacters(debouncedSearchTerm).then(results => {
        setIsSearching(false);
        setResults(results);
      });
    } else {
      setResults(null);
    }
  }, [debouncedSearchTerm]);
  return <div>
      <Margin size={{
      vertical: Size.DEFAULT
    }} mdxType="Margin">
        <SearchInput style={{
        maxWidth: 300
      }} loading={isSearching} placeholder="Search for marvel comic..." value={keywords} onChange={setKeywords} options={results?.map(res => ({
        value: res.id,
        label: res.title
      }))} onClickResult={id => {
        setComic(results.find(res => res.id === id));
      }} mdxType="SearchInput" />
      </Margin>
      <Text mdxType="Text">
        Selected comic:
      </Text>
      <div>
        {comic != null ? <Flex align={FlexAlign.START} mdxType="Flex">
            <FlexItem mdxType="FlexItem">
              <Margin mdxType="Margin">
                <img style={{
              maxWidth: 200,
              height: 200
            }} src={`${comic.thumbnail.path}.${comic.thumbnail.extension}`} />
              </Margin>
            </FlexItem>
            <FlexItem flex mdxType="FlexItem">
              <Title size={4} mdxType="Title">
                {comic.title}
              </Title>
              <Paragraph style={{
            color: sv.secondary
          }} mdxType="Paragraph">{comic.description ?? 'Description not available'}</Paragraph>
            </FlexItem>
          </Flex> : null}
      </div>
    </div>;
};

const layoutProps = {
  searchCharacters,
Example
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Search Input`}</h1>
<p>{`Usage`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { SearchInput } from '@drawbotics/react-drylus';
`}</code></pre>
<p>{`If you want to debounce, you should provide that logic yourself by creating a wrapper component that handles that. Any additional prop is passed to the input component.`}</p>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<div style={{ maxWidth: 300, height: 300 }}>
  <ControlledField initialValue="" component={
    <SearchInput onClickResult={() => {}} value="" placeholder="Search..." onChange={x=>x} options={[
      { label: 'Result 1', value: 1 },
      { label: 'Result 2', value: 2 },
      { label: 'Result 3', value: 3 },
      { label: 'Result 4', value: 4 },
      { label: 'Result 5', value: 5 },
      { label: 'Result 6', value: 6 },
    ]} />
  } />
</div>`} component={SearchInput} mdxType="Playground">
  <div style={{
        maxWidth: 300,
        height: 300
      }}>
    <ControlledField initialValue="" component={<SearchInput onClickResult={() => {}} value="" placeholder="Search..." onChange={x => x} options={[{
          label: 'Result 1',
          value: 1
        }, {
          label: 'Result 2',
          value: 2
        }, {
          label: 'Result 3',
          value: 3
        }, {
          label: 'Result 4',
          value: 4
        }, {
          label: 'Result 5',
          value: 5
        }, {
          label: 'Result 6',
          value: 6
        }]} mdxType="SearchInput" />} mdxType="ControlledField" />
  </div>
</Playground>
<p>{`If there are no results, the search displays a `}<inlineCode parentName="p">{`noResultLabel`}</inlineCode>{` which can be modified.`}</p>
<Playground __position={1} __code={`<div style={{ maxWidth: 300, height: 200 }}>
  <ControlledField initialValue="" component={
    <SearchInput value="" onChange={x=>x} options={[]} />
  } />
</div>`} mdxType="Playground">
  <div style={{
        maxWidth: 300,
        height: 200
      }}>
    <ControlledField initialValue="" component={<SearchInput value="" onChange={x => x} options={[]} mdxType="SearchInput" />} mdxType="ControlledField" />
  </div>
</Playground>
<h2>{`Example usage`}</h2>
<p>{`Here is an example usage with an external service. The `}<inlineCode parentName="p">{`onChange`}</inlineCode>{` functions triggers a new search, from which we get new matching results which are displayed in the dropdown. Selecting a result displays it on the page.`}</p>


<Preview mdxType="Preview">
  <Example mdxType="Example" />
</Preview>
<CodeBox type="mdx" mdxType="CodeBox">
{`
  const [comic, setComic] = useState();
  const [keywords, setKeywords] = useState('');
  const [results, setResults] = useState();
  const [isSearching, setIsSearching] = useState(false);
  const debouncedSearchTerm = useDebounce(keywords, 500);
  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      setIsSearching(true);
      searchCharacters(debouncedSearchTerm).then((results) => {
        setIsSearching(false);
        setResults(results);
      });
    } else {
      setResults(null);
    }
  }, [debouncedSearchTerm]);
  return (
    <div>
      <Margin size={{ vertical: Size.DEFAULT }}>
        <SearchInput
          style={{ maxWidth: 300 }}
          isLoading={isSearching}
          placeholder="Search for marvel comic..."
          value={keywords}
          onChange={setKeywords}
          options={results?.map((res) => ({
            value: res.id,
            label: res.title,
          }))}
          onClickResult={(id) => {
            setComic(results.find((res) => res.id === id))
          }} />
      </Margin>
      <Text>
        Selected comic:
      </Text>
      <div style={{ height: 300 }}>
        {comic != null ? (
          <Flex align={FlexAlign.START}>
            <FlexItem>
              <Margin>
                <img style={{ maxWidth: 200 }} src={comic.thumbnail} />
              </Margin>
            </FlexItem>
            <FlexItem flex>
              <Title size={4}>
                {comic.title}
              </Title>
              <Paragraph style={{ color: sv.secondary }}>{comic.description ?? 'Description not available'}</Paragraph>
            </FlexItem>
          </Flex>
        ) : null}
      </div>
    </div>
  );
`}
</CodeBox>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;